import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { schedulesService, groundsService } from '../services/supabaseService';
import { Trash2, Plus, ChevronDown, MapPin, AlertCircle, Download, X, Edit3, Users, Clock, Zap,  ClipboardCheck  } from 'lucide-react';
import PageLayout from './PageLayout';
import '../styles/ViewSchedule.css';
import '../styles/EditModal.css';
import QuickGameEditor from '../components/scheduleEditor/QuickGameEditor';
import VenueManager from '../components/scheduleEditor/VenueManager';
import DateScheduler from '../components/scheduleEditor/DateScheduler';
import TeamManager from '../components/scheduleEditor/TeamManager';
import BulkOperations from '../components/scheduleEditor/BulkOperations';
import ScheduleTracker from '../pages/ScheduleTracker';

const ViewSchedule = () => {
  useAuth(); // Keep hook call for authentication context
  const navigate = useNavigate();

  const [activeModal, setActiveModal] = useState(null);
  
  const [schedules, setSchedules] = useState([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Editor state
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [availableGrounds, setAvailableGrounds] = useState([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);

  // Helper function to format dates without timezone conversion
  const formatGameDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString();
  };

  const convertScheduleToCSV = (schedule) => {
    if (!schedule || !schedule.schedule_data || !Array.isArray(schedule.schedule_data)) {
      console.error('Invalid schedule data for CSV export:', schedule);
      return '';
    }

    const headers = [
      'Week',
      'Home Team',
      'Away Team',
      'Ground Name',
      'Ground City',
      'Ground State',
      'Ground Address 1',
      'Ground Address 2',
      'Ground Zip Code',
      'Ground Country',
      'Date',
      'Time'
    ];

    const csvRows = [headers.join(',')];

    try {
      const sortedWeeks = [...schedule.schedule_data].sort((a, b) => {
        return parseInt(a.week) - parseInt(b.week);
      });

      sortedWeeks.forEach(week => {
        if (week.games && Array.isArray(week.games)) {
          week.games.forEach(game => {
            const escapeCSV = (value) => {
              if (!value && value !== 0) return '';
              const stringValue = String(value);
              if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                return `"${stringValue.replace(/"/g, '""')}"`;
              }
              return stringValue;
            };

            const row = [
              week.week || '',
              escapeCSV(game.homeTeam),
              escapeCSV(game.awayTeam),
              escapeCSV(game.ground_name),
              escapeCSV(game.ground_city),
              escapeCSV(game.ground_state),
              escapeCSV(game.ground_address_1 || game.address_1),
              escapeCSV(game.ground_address_2 || game.address_2),
              escapeCSV(game.ground_zip_code || game.zip_code),
              escapeCSV(game.ground_country || game.country),
              escapeCSV(game.date),
              escapeCSV(game.time)
            ];
            csvRows.push(row.join(','));
          });
        }
      });

      return csvRows.join('\n');
    } catch (error) {
      console.error('Error converting schedule to CSV:', error);
      return '';
    }
  };

  const downloadCSV = (csvContent, filename) => {
    if (!csvContent) {
      alert('No data available for CSV export.');
      return;
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleDownloadCSV = async (schedule) => {
    try {
      const latestSchedule = await schedulesService.getById(schedule.id);
      const csvContent = convertScheduleToCSV(latestSchedule);
      
      if (!csvContent) {
        alert('Failed to generate CSV. The schedule may be empty or corrupted.');
        return;
      }
      
      const filename = `schedule-${generateDisplayId(schedule.id).replace('#', '')}-${new Date().toISOString().split('T')[0]}.csv`;
      downloadCSV(csvContent, filename);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to download CSV. Please try again.');
    }
  };

  const generateDisplayId = (scheduleId) => {
    return `#${scheduleId.slice(0, 7)}`;
  };

  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        setLoading(true);
        const data = await schedulesService.getAll();
        
        const sortedData = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        
        setSchedules(sortedData);
        
        if (sortedData.length > 0) {
          setSelectedScheduleId(sortedData[0].id);
        }
      } catch (err) {
        console.error('Error fetching schedules:', err);
        setError('Failed to load schedules. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSchedules();
  }, []);

  const fetchGrounds = async () => {
    try {
      const grounds = await groundsService.getAll();
      
      console.log('🏟️ First ground from database:', grounds[0]);
      
      const transformedGrounds = grounds.map(ground => ({
        id: ground.id,
        name: ground.ground_name,
        ground_name: ground.ground_name,
        city: ground.city,
        state: ground.state,
        address_1: ground.address_1,
        address_2: ground.address_2,
        zip_code: ground.zip_code,
        country: ground.country
      }));
      
      console.log('🏟️ First transformed ground:', transformedGrounds[0]);
      
      setAvailableGrounds(transformedGrounds);
    } catch (err) {
      console.error('Error fetching grounds:', err);
    }
  };

  const handleScheduleUpdate = async () => {
    try {
      console.log('🔄 handleScheduleUpdate called in ViewSchedule');
      
      const updatedSchedule = await schedulesService.getById(selectedScheduleId);
      
      console.log('📥 Fetched updated schedule from API:', updatedSchedule);
      
      setSchedules(prevSchedules => {
        const newSchedules = prevSchedules.map(s => 
          s.id === selectedScheduleId ? updatedSchedule : s
        );
        console.log('📊 Updated schedules state:', newSchedules);
        
        return [...newSchedules];
      });
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log('✅ Schedule state updated successfully');
    } catch (err) {
      console.error('❌ Error fetching updated schedule:', err);
      setError('Failed to update schedule data. Please refresh the page.');
    }
  };

  const handleDelete = async () => {
    if (!scheduleToDelete) return;
    
    try {
      await schedulesService.delete(scheduleToDelete.id);
      const updatedSchedules = schedules.filter(s => s.id !== scheduleToDelete.id);
      setSchedules(updatedSchedules);
      
      if (selectedScheduleId === scheduleToDelete.id) {
        if (updatedSchedules.length > 0) {
          setSelectedScheduleId(updatedSchedules[0].id);
        } else {
          setSelectedScheduleId(null);
        }
      }
      
      setShowDeleteModal(false);
      setScheduleToDelete(null);
    } catch (err) {
      console.error('Error deleting schedule:', err);
      alert('Failed to delete schedule. Please try again.');
      setShowDeleteModal(false);
      setScheduleToDelete(null);
    }
  };

  const handleScheduleSelect = (scheduleId) => {
    setSelectedScheduleId(scheduleId);
    setDropdownOpen(false);
    setShowEditPanel(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const selectedSchedule = schedules.find(s => s.id === selectedScheduleId);

  console.log('🎯 Current selectedSchedule:', {
    scheduleId: selectedSchedule?.id,
    dataLength: selectedSchedule?.schedule_data?.length,
    firstWeekGames: selectedSchedule?.schedule_data?.[0]?.games?.length,
    firstGame: selectedSchedule?.schedule_data?.[0]?.games?.[0],
    timestamp: new Date().toISOString()
  });

  const isGroundAssignmentEnabled = (schedule) => {
    if (schedule?.schedule_data) {
      for (const week of schedule.schedule_data) {
        if (week.games) {
          for (const game of week.games) {
            if (game.ground_id || game.ground_name) {
              return true;
            }
          }
        }
      }
    }
    
    return schedule?.ground_assignment_enabled || 
           schedule?.ground_assignment_method || 
           (schedule?.assigned_grounds && schedule.assigned_grounds.length > 0);
  };

  const getGroundAssignmentStats = (schedule) => {
    if (!schedule?.schedule_data) return { total: 0, withGrounds: 0, withoutGrounds: 0 };
    
    let total = 0;
    let withGrounds = 0;
    
    schedule.schedule_data.forEach(week => {
      if (week.games && Array.isArray(week.games)) {
        week.games.forEach(game => {
          total++;
          if (game.ground_id) withGrounds++;
        });
      }
    });
    
    const withoutGrounds = total - withGrounds;
    return { total, withGrounds, withoutGrounds };
  };

  const getAssignedGrounds = (schedule) => {
    if (!schedule?.schedule_data) return [];
    
    const groundsMap = new Map();
    
    schedule.schedule_data.forEach(week => {
      if (week.games && Array.isArray(week.games)) {
        week.games.forEach(game => {
          if (game.ground_id && !groundsMap.has(game.ground_id)) {
            groundsMap.set(game.ground_id, {
              id: game.ground_id,
              name: game.ground_name || `Ground ${game.ground_id.slice(0, 8)}`,
              city: game.ground_city || ''
            });
          }
        });
      }
    });
    
    return Array.from(groundsMap.values());
  };

  return (
    <PageLayout>
      <div className="view-schedule-container">
        <div className={`main-content ${showEditPanel ? 'with-edit-panel' : ''}`}>
          <div className="schedule-header">
            <div className="schedule-header-top">
              <div className="schedule-title-section">
                <h1>Your Schedules</h1>
                {schedules.length > 0 && (
                  <div className="header-dropdown-container">
                    <button 
                      className="dropdown-toggle"
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                    >
                      <span>
                        {selectedSchedule 
                          ? `Schedule ${generateDisplayId(selectedSchedule.id)} - ${formatDate(selectedSchedule.created_at)}`
                          : 'Select a schedule'
                        }
                      </span>
                      <ChevronDown 
                        size={20} 
                        className={`dropdown-arrow ${dropdownOpen ? 'rotated' : ''}`}
                      />
                    </button>
                    
                    {dropdownOpen && (
                      <div className="dropdown-menu">
                        {schedules.map((schedule) => (
                          <div 
                            key={schedule.id}
                            className={`dropdown-item ${schedule.id === selectedScheduleId ? 'selected' : ''}`}
                            onClick={() => handleScheduleSelect(schedule.id)}
                          >
                            <div className="schedule-option">
                              <span className="schedule-id">
                                Schedule {generateDisplayId(schedule.id)}
                              </span>
                              <span className="schedule-date">
                                {formatDate(schedule.created_at)}
                              </span>
                              <span className="schedule-ground-status">
                                {isGroundAssignmentEnabled(schedule) ? '🏟️ With Grounds' : '📋 Schedule Only'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button 
                className="create-schedule-btn"
                onClick={() => navigate('/CreateSchedule')}
              >
                <Plus size={20} />
                Create New Schedule
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading schedules...</p>
            </div>
          ) : (
            <div className="schedule-content">
              <div className="schedules-list">
                {schedules.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">📅</div>
                    <h3>No schedules yet</h3>
                    <p>Create your first schedule to get started</p>
                    <button 
                      className="create-first-schedule-btn"
                      onClick={() => navigate('/CreateSchedule')}
                    >
                      <Plus size={20} />
                      Create Your First Schedule
                    </button>
                  </div>
                ) : (
                  <div className="schedule-display">
                    <div className="schedule-selector">
                      <div className="dropdown-container">
                        <button 
                          className="dropdown-toggle"
                          onClick={() => setDropdownOpen(!dropdownOpen)}
                        >
                          <span>
                            {selectedSchedule 
                              ? `Schedule ${generateDisplayId(selectedSchedule.id)} - ${formatDate(selectedSchedule.created_at)}`
                              : 'Select a schedule'
                            }
                          </span>
                          <ChevronDown 
                            size={20} 
                            className={`dropdown-arrow ${dropdownOpen ? 'rotated' : ''}`}
                          />
                        </button>
                        
                        {dropdownOpen && (
                          <div className="dropdown-menu">
                            {schedules.map((schedule) => (
                              <div 
                                key={schedule.id}
                                className={`dropdown-item ${schedule.id === selectedScheduleId ? 'selected' : ''}`}
                                onClick={() => handleScheduleSelect(schedule.id)}
                              >
                                <div className="schedule-option">
                                  <span className="schedule-id">
                                    Schedule {generateDisplayId(schedule.id)}
                                  </span>
                                  <span className="schedule-date">
                                    {formatDate(schedule.created_at)}
                                  </span>
                                  <span className="schedule-ground-status">
                                    {isGroundAssignmentEnabled(schedule) ? '🏟️ With Grounds' : '📋 Schedule Only'}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {selectedSchedule && (
                      <div className="schedule-container">
                        <div className="schedule-header-simple">
                          <div className="schedule-title-section">
                            <h2>Schedule {generateDisplayId(selectedSchedule.id)}</h2>
                          </div>
                          <div className="schedule-actions">
                            <span className="schedule-created">
                              Created: {formatDate(selectedSchedule.created_at)}
                            </span>
                            <button 
                              className="action-btn delete-btn"
                              onClick={() => {
                                setScheduleToDelete(selectedSchedule);
                                setShowDeleteModal(true);
                              }}
                              title="Delete Schedule"
                            >
                              <Trash2 size={18} />
                              Delete
                            </button>
                            <button 
                              className="action-btn quick-edit-btn"
                              onClick={async () => {
                                await fetchGrounds();
                                setActiveModal('quickEdit');
                              }}
                              title="Quick Game Editor"
                            >
                              <Edit3 size={16} />
                            </button>

                            <button 
                              className="action-btn venue-btn"
                              onClick={async () => {
                                await fetchGrounds();
                                setActiveModal('venue');
                              }}
                              title="Venue Manager"
                            >
                              <MapPin size={16} />
                            </button>

                            <button 
                              className="action-btn date-btn"
                              onClick={() => setActiveModal('date')}
                              title="Date Scheduler"
                            >
                              <Clock size={16} />
                            </button>

                            <button 
                              className="action-btn team-btn"
                              onClick={() => setActiveModal('team')}
                              title="Team Manager"
                            >
                              <Users size={16} />
                            </button>

                            <button 
                              className="action-btn bulk-btn"
                              onClick={async () => {
                                await fetchGrounds();
                                setActiveModal('bulk');
                              }}
                              title="Bulk Operations"
                            >
                              <Zap size={16} />
                            </button>
                            <button 
                              className="action-btn tracker-btn"
                              onClick={() => setActiveModal('tracker')}
                              title="Schedule Checker"
                            >
                              <ClipboardCheck size={16} />
                            </button>
                            <button 
                              className="action-btn csv-btn"
                              onClick={() => handleDownloadCSV(selectedSchedule)}
                              title="Download CSV"
                            >
                              <Download size={16} />
                            </button>
                          </div>
                        </div>

                        <div className="ground-status-info">
                          {isGroundAssignmentEnabled(selectedSchedule) ? (
                            <div className="ground-assignment-enabled">
                              <MapPin size={16} />
                              <span>
                                {(() => {
                                  const stats = getGroundAssignmentStats(selectedSchedule);
                                  const assignedGrounds = getAssignedGrounds(selectedSchedule);
                                  return `${stats.withGrounds} of ${stats.total} games assigned to ${assignedGrounds.length} ground${assignedGrounds.length !== 1 ? 's' : ''}`;
                                })()}
                              </span>
                            </div>
                          ) : (
                            <div className="ground-assignment-disabled">
                              <AlertCircle size={16} />
                              <span>No ground assignments</span>
                            </div>
                          )}
                        </div>

                        {isGroundAssignmentEnabled(selectedSchedule) && (
                          <div className="ground-summary">
                            <h4>Assigned Grounds</h4>
                            <div className="grounds-list">
                              {getAssignedGrounds(selectedSchedule).map((ground, index) => (
                                <div key={ground.id} className="ground-item">
                                  <MapPin size={14} />
                                  <span>{ground.name}</span>
                                  {ground.city && <span className="ground-city">({ground.city})</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <div className="game-schedule">
                          <h3>Game Schedule</h3>
                          {selectedSchedule.schedule_data && selectedSchedule.schedule_data.length > 0 ? (
                            <div className="weeks-grid">
                              {[...selectedSchedule.schedule_data]
                                .sort((a, b) => parseInt(a.week) - parseInt(b.week))
                                .map((week, weekIndex) => (
                                <div key={weekIndex} className="week-column">
                                  <h4>Week {week.week}</h4>
                                  <div className="games-list">
                                    {week.games && Array.isArray(week.games) ? week.games.map((game, gameIndex) => (
                                      <div key={gameIndex} className="game-matchup">
                                        <div className="team-names">
                                          <span className="team">{game.homeTeam}</span>
                                          <span className="vs">vs</span>
                                          <span className="team">{game.awayTeam}</span>
                                        </div>
                                        
                                        {isGroundAssignmentEnabled(selectedSchedule) ? (
                                          game.ground_name ? (
                                            <div className="game-venue">
                                              <MapPin size={14} />
                                              <div className="venue-info">
                                                <span className="venue-name">{game.ground_name}</span>
                                                {game.ground_city && (
                                                  <span className="venue-city">({game.ground_city})</span>
                                                )}
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="game-venue no-venue">
                                              <MapPin size={14} />
                                              <span className="venue-tbd">No ground assigned</span>
                                            </div>
                                          )
                                        ) : (
                                          <div className="game-venue no-ground-system">
                                            <span className="venue-disabled">Ground assignment disabled</span>
                                          </div>
                                        )}
                                        
                                        {(game.date || game.time) && (
                                          <div className="game-datetime">
                                            {game.date && (
                                              <span className="game-date">
                                                {formatGameDate(game.date)}
                                              </span>
                                            )}
                                            {game.time && (
                                              <span className="game-time">{game.time}</span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )) : (
                                      <p>No games scheduled for this week</p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="no-schedule-data">
                              <p>No schedule data available</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Individual Modals */}
        {activeModal === 'quickEdit' && selectedSchedule && (
          <div className="modal-overlay" onClick={() => setActiveModal(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <QuickGameEditor 
                selectedSchedule={selectedSchedule}
                availableGrounds={availableGrounds}
                schedulesService={schedulesService}
                onScheduleUpdate={handleScheduleUpdate}
                onClose={() => setActiveModal(null)}
              />
            </div>
          </div>
        )}

        {activeModal === 'venue' && selectedSchedule && (
          <div className="modal-overlay" onClick={() => setActiveModal(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <VenueManager 
                selectedSchedule={selectedSchedule}
                availableGrounds={availableGrounds}
                schedulesService={schedulesService}
                onScheduleUpdate={handleScheduleUpdate}
                onClose={() => setActiveModal(null)}
              />
            </div>
          </div>
        )}

        {activeModal === 'date' && selectedSchedule && (
          <div className="modal-overlay" onClick={() => setActiveModal(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <DateScheduler 
                selectedSchedule={selectedSchedule}
                schedulesService={schedulesService}
                onScheduleUpdate={handleScheduleUpdate}
                onClose={() => setActiveModal(null)}
              />
            </div>
          </div>
        )}

        {activeModal === 'team' && selectedSchedule && (
          <div className="modal-overlay" onClick={() => setActiveModal(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <TeamManager 
                selectedSchedule={selectedSchedule}
                schedulesService={schedulesService}
                onScheduleUpdate={handleScheduleUpdate}
                onClose={() => setActiveModal(null)}
              />
            </div>
          </div>
        )}

        {activeModal === 'bulk' && selectedSchedule && (
          <div className="modal-overlay" onClick={() => setActiveModal(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <BulkOperations 
                selectedSchedule={selectedSchedule}
                availableGrounds={availableGrounds}
                schedulesService={schedulesService}
                onScheduleUpdate={handleScheduleUpdate}
                onClose={() => setActiveModal(null)}
              />
            </div>
          </div>
        )}

        {/* Schedule Tracker Modal - No wrapper needed */}
        {activeModal === 'tracker' && selectedSchedule && (
          <ScheduleTracker 
            selectedSchedule={selectedSchedule}
            onClose={() => setActiveModal(null)}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && scheduleToDelete && (
          <div 
            className="modal-overlay delete-modal-overlay" 
            onClick={() => {
              setShowDeleteModal(false);
              setScheduleToDelete(null);
            }}
          >
            <div 
              className="modal-content delete-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-modal-header">
                <div className="delete-modal-icon">
                  <AlertCircle size={24} />
                </div>
                <h3>Delete Schedule</h3>
                <button
                  className="modal-close-btn"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setScheduleToDelete(null);
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="delete-modal-body">
                <p className="delete-warning-text">
                  Are you sure you want to delete <strong>Schedule {generateDisplayId(scheduleToDelete.id)}</strong>?
                </p>
                <p className="delete-subtext">
                  This action cannot be undone. All games, assignments, and schedule data will be permanently removed.
                </p>
              </div>

              <div className="delete-modal-footer">
                <button
                  className="cancel-delete-btn"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setScheduleToDelete(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="confirm-delete-btn"
                  onClick={handleDelete}
                >
                  Delete Schedule
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default ViewSchedule;